/**
 * Copyright IBM Corp. 2021-2024
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */
import debounce from "lodash/debounce";
import TriFloorPlanPlugin from "./TriFloorPlanPlugin.js";
import TriFloorPlanAPI from "../api/TriFloorPlanAPI.js";

/**
 * autoScale:       If true, the labels are scaled connter to the floor plan 
 *                  scaling so they maintaine a constant size as seen by the user
 * hideOverlapping: If true labels are selective hidden to prevent labels from
 *                  overlapping on screen.  
 * labelId
 * magnification
 */
export default class TriLabelsPlugin extends TriFloorPlanPlugin {
    static TEXT_PIXEL_HEIGHT = 20;    // This seems to produce the 8pt specified for labels
    static type = "labels";

    constructor() {
        super();
        this.magnification = 1;
        this.textPixelHeight = TriLabelsPlugin.TEXT_PIXEL_HEIGHT;
        this.handleMouseMove = this.handleMouseMove.bind( this );
    }

    pluginDidUpdate()
    {
        const {
            floorPlanLoaded,
            enabled,
            hideOverlapping,
            labelId,
            magnification,
        } = this.props;

        if( !floorPlanLoaded || !enabled )
        {
            this.removeMouseMoveListener();
            this.clearLabelsLayer();
            this.lastLabelId = null;
            this.labelGroup = null;
            return;
        }

        if( !this.hoverListenerAttached )
        {
            const { svgElement } = this.props.triFloorPlan;
            if( svgElement )
            {
                svgElement.addEventListener( 'mousemove', this.handleMouseMove );
                this.hoverListenerAttached = true;
            }
        }

        if( typeof this.props.pointSize === "number" )
        {
            this.textPixelHeight = TriLabelsPlugin.TEXT_PIXEL_HEIGHT / 8 * this.props.pointSize;
        }

        if( labelId !== this.lastLabelId )
        {
            this.lastLabelId = labelId;
          this.updateLabels();

        }

        let refreshScale = false;
        let refreshOverlap = false;

        if( this.textClass !== this.props.textClass )
        {
            const labelLayerElement = this.getLabelLayerElement();
            if( labelLayerElement )
            {
                this.setLabelClass(labelLayerElement, this.props.textClass);
                refreshOverlap = true;
            }
        }
        this.textClass = this.props.textClass;

        if( this.hideOverlapping !== hideOverlapping )
        {
            refreshOverlap = true;
        }
        this.hideOverlapping = hideOverlapping;

        if( this.autoScale !== this.props.autoScale )
        {
            refreshScale = true;
            refreshOverlap = true;
        }
        this.autoScale = this.props.autoScale;

        if( this.pointSize !== this.props.pointSize )
        {
            refreshScale = true;
            refreshOverlap = true;
        }
        this.pointSize = this.props.pointSize

        if( this.magnification !== magnification && magnification )
        {
            refreshScale = true;
            refreshOverlap = true;
            this.magnification = this.props.magnification;
        }

        if( refreshScale )
        {
            this.scaleLabels(this.curretViewBox, true);
        }

        if( refreshOverlap )
        {
            this.hideOverlappingLabels(this.curretViewBox, true);
        }
    }

    pluginDidRemove()
    {
        this.removeMouseMoveListener();
        this.clearLabelsLayer();
    }

    floorPlanDidPanOrZoom(newViewBox)
    {
        this.updateLabelScale(newViewBox);
    }

    updateLabelScale = debounce( ( newViewBox ) => {
        if (this.props.autoScale)
        {
            this.scaleLabels(newViewBox);
            this.hideOverlappingLabels(newViewBox);
            this.curretViewBox = newViewBox;
        }
    }, 100);

    async updateLabels()
    {
        this.clearLabelsLayer();
        const {
            triFloorPlan,
            labelId,
            onLoadError,
            textClass
        } = this.props;

        const { initialSvgViewBox } = triFloorPlan;

        const floorPlanId = triFloorPlan.floorPlanId;
        let labelLayerElement = this.getLabelLayerElement();
        if (!labelLayerElement) return;

        let floorplanLabels = {};
        try {
            floorplanLabels = await TriFloorPlanAPI.getLabelsLayer(
                floorPlanId,
                labelId
            );
        }
        catch (e)
        {
            if( onLoadError ) onLoadError( e );
            return;
        }

        if (!floorplanLabels) return;

        labelLayerElement.innerHTML = floorplanLabels;

        this.labelGroup = this.flatten(labelLayerElement.children);

        if (textClass)
        {
            this.setLabelClass(labelLayerElement, textClass);
        }

        this.bBoxMap = new Map();
        const labels = this.labelGroup.children;
        for (let i = 0; i < labels.length; i++)
        {
            this.bBoxMap.set(labels[i], labels[i].getBBox() )
        }

        if( this.props.autoScale )
        {
            let viewBox = this.curretViewBox;
            if( !viewBox ) viewBox = initialSvgViewBox;
            this.curretViewBox = initialSvgViewBox;
            this.scaleLabels( viewBox, true );
        }

        if( this.props.hideOverlapping )
        {
            this.hideOverlappingLabels( this.curretViewBox, true );
        }
    }

    setLabelClass(el, textClass)
    {
        if( el.tagName === "text" ) el.className.baseVal = textClass;

        const children = el.children;
        for (let i = 0; i < children.length; i++)
        {
            this.setLabelClass( children[i], textClass );
        }
    }

    getLabelLayerElement()
    {
        const { triFloorPlan, enabled, floorPlanLoaded } = this.props;
        const { svgElement } = triFloorPlan;
        if( !floorPlanLoaded || !enabled || !svgElement ) return null;

        return svgElement.querySelector("#_triLabelLayer");
    }

    /**
     * Remove g elements that have no children to increase scaling efficiency
     * @param {any} children
     * @returns
     */
    flatten( children )
    {
        let labelGroup;
        for( let i = 0; i < children.length; i++ )
        {
            const el = children[i];
            if (el.tagName === "g")
            {
                if (el.id === "==LabelsLayer")
                {
                    labelGroup = el;
                    break;
                }
            }
        }

        if( !labelGroup ) return;

        for( let i = 0; i < labelGroup.children.length; i++ )
        {
            const el = labelGroup.children[i];
            if( el.tagName === "g" )
            {
                if (el.children.length > 0) continue;
                el.parentNode.removeChild(el);
            }
        }

        return labelGroup;
    }

    clearLabelsLayer()
    {
        const { floorPlanLoaded, triFloorPlan } = this.props;
        if( !floorPlanLoaded ) return;
        const { svgElement } = triFloorPlan;
        if( !svgElement ) return;

        const labelLayerEl = svgElement.querySelector("#_triLabelLayer");
        if( labelLayerEl )
        {
            labelLayerEl.innerHTML = "";
        }
    }

    hideOverlappingLabels( newViewBox, force )
    {
        if( !this.labelGroup || !this.curretViewBox || !newViewBox ) return;

        if( !this.props.hideOverlapping )
        {
            const labels = this.labelGroup.children;
            for( let i = 0; i < labels.length; i++ )
            {
                labels[i].style.display = "";
            }
            return;
        }

        // Nothing has changed
        if(    this.curretViewBox.scaleX === newViewBox.scaleX
            && this.curretViewBox.scaleY === newViewBox.scaleY )
        {
            if( force !== true ) return;
        }

        const height = this.getSvgLabelHeight();

        const labels = this.labelGroup.children;
        const labelList = [];
        for( let i = 0; i < labels.length; i++ )
        {
            const bBox = this.bBoxMap.get(labels[i]);
            const status = {
                bBox:     {},
                label:    labels[i],
                isHidden: false,
            }

            status.bBox.x      = bBox.x,
            status.bBox.y      = bBox.y,
            status.bBox.width  = bBox.width;
            status.bBox.height = bBox.height;

            if (this.props.autoScale)
            {
                const scale = ( bBox.height !== 0 ? height / bBox.height : 1 ) * this.magnification;

                status.bBox.width *= scale;
                status.bBox.height *= scale;
            }

            labelList.push(status);
        }

        for (let i = 0; i < labelList.length; i++)
        {
            if( labelList[i].isHidden ) continue;

            for (let j = i + 1; j < labelList.length; j++)
            {
                if( labelList[j].isHidden ) continue;

                const bb1 = labelList[i].bBox;
                const bb2 = labelList[j].bBox;
                if(    bb1.x + bb1.width < bb2.x     // 1 left of 2
                    || bb1.y + bb1.height < bb2.y    // 1 above 2
                    || bb1.x > bb2.x + bb2.width     // 1 right of 2
                    || bb1.y > bb2.y + bb2.height)   // 2 below 2
                {
                    labelList[j].label.style.display = "";
                    continue;
                }
                labelList[j].isHidden = true;
                labelList[j].label.style.display = "none";
            }
        }
    }

    scaleLabels( newViewBox, force )
    {
        if( !this.labelGroup || !this.labelGroup.parentNode || !newViewBox ) return;

        const {
            triFloorPlan,
            enabled,
            autoScale,
        } = this.props;

        const svgViewPort = triFloorPlan.svgViewPort;
        if(    !enabled
            || !svgViewPort.width
            || !svgViewPort.height
        ) {
            return;
        }

        // Nothing has changed
        if(    this.curretViewBox.scaleX === newViewBox.scaleX
            && this.curretViewBox.scaleY === newViewBox.scaleY )
        {
            if( force !== true ) return;
        }

        const labelElementList = this.labelGroup.children;
        if( labelElementList.length === 0 ) return;

        const height = this.getSvgLabelHeight();

        const { svgElement } = triFloorPlan;
        const transforms = [];
        if( autoScale )
        {
            for( let i = 0; i < labelElementList.length; i++ )
            {
                const label = labelElementList[i];
                const bBox = this.bBoxMap.get(label);
                const scale = ( bBox.height !== 0 ? height / bBox.height : 1 ) * this.magnification;

                let transform = svgElement.createSVGTransform();
                let matrix = svgElement.createSVGMatrix();
                // Translate to origin so scaling won't scale offset
                matrix = matrix.translate(bBox.x, bBox.y);
                // Scale
                matrix = matrix.scale(scale, scale);
                // Put it back
                matrix = matrix.translate(-bBox.x, -bBox.y);
                transform.setMatrix(matrix);

                transforms.push(transform);
            }
        }

        // Do all the transforms with the parent not in the DOM so each transform isn't
        // rendered seperatly
        const parent = this.labelGroup.parentNode;
        parent.removeChild( this.labelGroup );

        for( let i = 0; i < labelElementList.length; i++ )
        {
            const label = labelElementList[i];
            label.transform.baseVal.clear();
            if( autoScale )
            {
                label.transform.baseVal.appendItem(transforms[i]);
            }
        }

        parent.appendChild( this.labelGroup );
    }

    getSvgLabelHeight()
    {
        const { triFloorPlan } = this.props;
        const { svgElement } = triFloorPlan;

        const matrix = svgElement.getScreenCTM().inverse();

        const top = svgElement.createSVGPoint();
        const svgTop = top.matrixTransform( matrix );
        const bottom = svgElement.createSVGPoint();
        bottom.y = this.textPixelHeight;
        const svgBottom = bottom.matrixTransform( matrix );

        return svgBottom.y - svgTop.y;
    }

    handleMouseMove( evt )
    {
        const { triFloorPlan, onHover } = this.props;
        if( !onHover ) return;

        const { svgElement, svgViewPort } = triFloorPlan;

        const matrix = svgElement.getScreenCTM().inverse();
        const point = svgElement.createSVGPoint();
        point.x = svgViewPort.left + evt.offsetX;
        point.y = svgViewPort.top + evt.offsetY;
        const svgPoint = point.matrixTransform( matrix );

        const space = this.hitTest( svgPoint );
        const spaceId = space ? space.getAttribute( TriFloorPlanPlugin.NAME_PROPERTY ) : null;

        if( spaceId === this.lastHoveredSpaceId ) return;
        this.lastHoveredSpaceId = spaceId;
        onHover( spaceId );
    }

    removeMouseMoveListener()
    {
        if( !this.hoverListenerAttached ) return;
        const { triFloorPlan } = this.props;
        if( !triFloorPlan ) return;
        const { svgElement } = triFloorPlan;
        if( !svgElement ) return;
        svgElement.removeEventListener( 'mousemove', this.handleMouseMove );
        this.hoverListenerAttached = false;
    }
}
